import { ISO2TO3 } from "constants/countries";

const CITY_FALLBACK_TYPES = [
  "administrative_area_level_2",
  "administrative_area_level_3",
];

// Some places, such as a building with no street number, carry no route
// components, so street 1 is taken from the formatted address up to the city
// or, without a city, the region.
const streetFromFormattedAddress = (formattedAddress, city, region) => {
  const anchor = city || region;
  if (!anchor || !formattedAddress) return "";
  const match = new RegExp(`^(?<street>.+),\\s+${anchor}`).exec(
    formattedAddress
  );
  return match?.groups?.street ?? "";
};

/**
 * Converts a Places `PlaceResult` into the address fields the API stores.
 * Countries are stored as ISO 3166-1 alpha-3 codes, while Places reports
 * alpha-2.
 *
 * `pickedStreet` is the street text of the suggestion the user chose. It is
 * used when the place has no street components, which happens when Google
 * resolves a street address to its postal code area. Reading the street from
 * the formatted address then returns the neighborhood instead.
 */
export const parsePlaceAddress = (place, pickedStreet = "") => {
  let street1 = "";
  let street2 = "";
  let city = "";
  let postal_code = "";
  let region = "";
  let country = "";

  for (const component of place.address_components ?? []) {
    switch (component.types[0]) {
      case "premise":
      case "street_number":
        street1 = `${component.long_name} ${street1}`;
        break;
      case "route":
        street1 += component.short_name;
        break;
      case "subpremise":
        street2 = `${component.long_name} ${street2}`;
        break;
      case "postal_code":
        postal_code = `${component.long_name}${postal_code}`;
        break;
      case "postal_code_suffix":
        postal_code = `${postal_code}-${component.long_name}`;
        break;
      case "locality":
        city = component.long_name;
        break;
      case "administrative_area_level_1":
        region = component.long_name;
        break;
      case "country":
        country = ISO2TO3[component.short_name] ?? "";
        break;
      default:
        break;
    }
  }

  // The city resolves first, because the street fallback anchors on it.
  if (!city) {
    city =
      place.address_components?.find(({ types }) =>
        CITY_FALLBACK_TYPES.includes(types[0])
      )?.long_name ?? "";
  }
  if (!street1) {
    street1 =
      pickedStreet ||
      streetFromFormattedAddress(place.formatted_address, city, region);
  }

  const location = place.geometry?.location;

  return {
    street1: street1.trim(),
    street2: street2.trim(),
    city,
    region,
    postal_code,
    country,
    latitude: location ? location.lat() : null,
    longitude: location ? location.lng() : null,
    info: place,
  };
};
